import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardService } from '../../api/apiClient';
import { useAuth } from '../../context/AuthContext';
import { StatCard } from '../../components/UI/StatCard';
import { Card, CardHeader, CardTitle, CardBody } from '../../components/UI/Card';
import { Badge } from '../../components/UI/Badge';
import { Button } from '../../components/UI/Button';
import { Spinner } from '../../components/UI/Spinner';
import {
  TrendingUp,
  FileText,
  CheckCircle,
  Clock,
  Plus,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

export const Dashboard = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const response = await dashboardService.getStats();
        if (response && response.data) {
          setData(response.data);
        }
      } catch (err) {
        console.error('Error loading dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading) {
    return <Spinner size="lg" label="Cargando estadísticas..." className="py-24" />;
  }

  const counters = data?.counters || {};
  const charts = data?.charts || {};
  const recentInvoices = data?.recentInvoices || [];
  const lowStockProducts = data?.lowStockProducts || [];

  const months = charts.months || [];
  const monthlyData = charts.data || [];
  const maxRevenue = Math.max(...monthlyData, 100);

  return (
    <div className="space-y-6">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-[#08707A] via-[#065B63] to-[#04464C] rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
        <div className="absolute right-0 top-0 bottom-0 opacity-10 flex items-center pr-8 pointer-events-none">
          <Sparkles className="w-48 h-48" />
        </div>

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1">
            <h1 className="text-xl sm:text-2xl font-black tracking-tight">
              ¡Hola de nuevo, {user?.name || 'Usuario'}!
            </h1>
            <p className="text-xs sm:text-sm text-teal-100 max-w-xl">
              Aquí tienes el resumen financiero y operativo de tu negocio para este periodo.
            </p>
          </div>

          <div className="flex items-center gap-2.5">
            <Link to="/invoices/create">
              <Button
                variant="secondary"
                size="sm"
                icon={Plus}
                className="bg-white text-[#08707A] hover:bg-teal-50 border-none font-bold"
              >
                Nueva Factura
              </Button>
            </Link>
            <Link to="/quotes/create">
              <Button
                variant="ghost"
                size="sm"
                icon={Plus}
                className="text-white hover:bg-white/10 border border-white/20"
              >
                Nueva Cotización
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Facturación del Mes"
          value={`$${Number(counters.thisMonthRevenue || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          subtitle="Generado este mes"
          icon={TrendingUp}
          color="brand"
        />

        <StatCard
          title="Total Facturado"
          value={`$${Number(counters.totalInvoicedAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          subtitle="Suma histórica facturada"
          icon={FileText}
          color="blue"
        />

        <StatCard
          title="Total Pagado"
          value={`$${Number(counters.totalPaidAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          subtitle="Cobros recibidos"
          icon={CheckCircle}
          color="emerald"
        />

        <StatCard
          title="Saldo Pendiente"
          value={`$${Number(counters.globalPendingAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}`}
          subtitle="Por cobrar"
          icon={Clock}
          color="amber"
        />
      </div>

      {/* Chart and Low Stock Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Bar Chart (2 columns) */}
        <Card className="lg:col-span-2">
          <CardHeader
            actions={
              <Badge variant="primary" size="sm">
                Últimos 6 meses
              </Badge>
            }
          >
            <CardTitle subtitle="Ingresos generados por facturación mensual">
              Rendimiento Financiero
            </CardTitle>
          </CardHeader>

          <CardBody>
            <div className="h-64 flex items-end justify-between gap-3 sm:gap-6 pt-6 pb-2 px-2">
              {months.map((m, idx) => {
                const val = monthlyData[idx] || 0;
                const heightPercent = Math.max(6, Math.round((val / maxRevenue) * 100));

                return (
                  <div key={m} className="flex-1 flex flex-col items-center gap-2 group h-full justify-end">
                    {/* Tooltip on hover */}
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-slate-900 text-white text-[10px] font-bold py-1 px-2 rounded-md shadow pointer-events-none whitespace-nowrap mb-1">
                      ${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </div>

                    {/* Bar */}
                    <div className="w-full max-w-[48px] bg-slate-100 dark:bg-slate-800 rounded-t-xl overflow-hidden flex flex-col justify-end h-full">
                      <div
                        style={{ height: `${heightPercent}%` }}
                        className="w-full bg-gradient-to-t from-teal-800 to-[#08707A] group-hover:from-teal-600 group-hover:to-teal-500 rounded-t-xl transition-all duration-300 shadow-sm"
                      />
                    </div>

                    {/* Month Label */}
                    <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">
                      {m}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardBody>
        </Card>

        {/* Low Stock Alerts (1 column) */}
        <Card>
          <CardHeader
            actions={
              <Link to="/inventory">
                <Button variant="ghost" size="sm" icon={ArrowRight}>
                  Ver Todo
                </Button>
              </Link>
            }
          >
            <CardTitle subtitle="Artículos cerca del umbral mínimo">
              Alertas de Stock
            </CardTitle>
          </CardHeader>

          <CardBody className="p-0">
            {lowStockProducts.length === 0 ? (
              <div className="p-8 text-center text-xs text-slate-400">
                Inventario óptimo. No hay artículos con stock crítico.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {lowStockProducts.map((p) => (
                  <div key={p.id} className="p-4 flex items-center justify-between gap-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/30">
                    <div className="space-y-0.5">
                      <p className="text-xs font-bold text-slate-900 dark:text-slate-100">
                        {p.name}
                      </p>
                      <p className="text-[11px] text-slate-400 font-mono">
                        SKU: {p.sku || 'N/A'}
                      </p>
                    </div>

                    <div className="text-right">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-black bg-rose-50 text-rose-700 dark:bg-rose-950/60 dark:text-rose-400 border border-rose-200 dark:border-rose-900/60 font-mono">
                        {p.stock} unid.
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Recent Invoices List */}
      <Card>
        <CardHeader
          actions={
            <Link to="/invoices">
              <Button variant="ghost" size="sm" icon={ArrowRight}>
                Ver Facturas
              </Button>
            </Link>
          }
        >
          <CardTitle subtitle="Últimos comprobantes emitidos en el sistema">
            Facturas Recientes
          </CardTitle>
        </CardHeader>

        <CardBody className="p-0">
          {recentInvoices.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-400">
              No has emitido facturas recientemente.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800">
                  <tr>
                    <th className="py-3 px-4">Número</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Fecha</th>
                    <th className="py-3 px-4">Total</th>
                    <th className="py-3 px-4 text-center">Estado</th>
                    <th className="py-3 px-4 text-right">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {recentInvoices.map((inv) => (
                    <tr key={inv.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-900 dark:text-slate-100">
                        #{inv.number || String(inv.id).padStart(6, '0')}
                      </td>
                      <td className="py-3.5 px-4 font-medium text-slate-800 dark:text-slate-200">
                        {inv.customer?.full_name || 'Cliente sin nombre'}
                      </td>
                      <td className="py-3.5 px-4 text-slate-500 dark:text-slate-400">
                        {inv.invo_date}
                      </td>
                      <td className="py-3.5 px-4 font-bold text-slate-900 dark:text-slate-100 font-mono">
                        ${Number(inv.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        {inv.status === 1 ? (
                          <Badge variant="success" size="sm" dot>Pagada</Badge>
                        ) : (
                          <Badge variant="warning" size="sm" dot>Abierta</Badge>
                        )}
                      </td>
                      <td className="py-3.5 px-4 text-right">
                        <Link to={`/invoices/${inv.id}`}>
                          <Button variant="ghost" size="sm">
                            Ver
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
